import { useState, useEffect, useRef } from "react"
import {
  Dropdown,
  InputField,
  TextArea,
  Button,
  ButtonSize,
} from "../../../../components"
import { useTextStorage } from "../../../../hooks"
import classNames from "classnames"
import { mapServiceKeyToServiceLabel } from "../../../../utils/multilang-text-storage"
import { config } from "../../../../utils/config"
import {
  testimonial1NameTag as connectFormDecor,
  connectFormDecor2,
  testimonialDecor2 as connectFormDecor3,
} from "../../../../assets/images"

declare global {
  interface Window {
    grecaptcha: {
      ready: (callback: () => void) => void
      render: (
        container: HTMLElement,
        options: { sitekey: string; callback?: (token: string) => void },
      ) => number
      reset: (widgetId?: number) => void
      getResponse: (widgetId?: number) => string
    }
  }
}

export const ConnectForm: React.FC = () => {
  const { ts } = useTextStorage()
  const [name, setName] = useState("")
  const [phoneNumber, setPhoneNumber] = useState("")
  const [email, setEmail] = useState("")
  const [message, setMessage] = useState("")
  const [service, setService] = useState("")
  const [recaptchaToken, setRecaptchaToken] = useState("")

  const [justSubmitted, setJustSubmitted] = useState(false)
  const [isRequestPending, setIsRequestPending] = useState(false)
  const [error, setError] = useState("")

  const recaptchaRef = useRef<HTMLDivElement>(null)
  const widgetIdRef = useRef<number | null>(null)

  const allRequiredFieldsFilled =
    name.trim() !== "" &&
    phoneNumber.trim() !== "" &&
    email.trim() !== "" &&
    service !== ""

  const clearAllFormFields = () => {
    setName("")
    setPhoneNumber("")
    setEmail("")
    setMessage("")
    setService("")
    setRecaptchaToken("")
  }

  useEffect(() => {
    const renderRecaptcha = () => {
      if (recaptchaRef.current && widgetIdRef.current === null) {
        widgetIdRef.current = window.grecaptcha.render(recaptchaRef.current, {
          sitekey: config.recaptchaSitekey,
          callback: (token: string) => setRecaptchaToken(token),
        })
      }
    }

    // Wait for grecaptcha to be fully loaded
    if (window.grecaptcha?.ready) {
      window.grecaptcha.ready(renderRecaptcha)
    } else {
      // Script not yet loaded, wait for it
      const checkInterval = setInterval(() => {
        if (window.grecaptcha?.ready) {
          clearInterval(checkInterval)
          window.grecaptcha.ready(renderRecaptcha)
        }
      }, 100)

      return () => clearInterval(checkInterval)
    }
  }, [])

  function submitFormData(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()

    if (!recaptchaToken) {
      setError("Please complete the reCAPTCHA")
      return
    }

    setIsRequestPending(true)

    fetch(config.formcarryUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        name,
        phone: phoneNumber,
        email,
        service: mapServiceKeyToServiceLabel(service),
        message,
        "g-recaptcha-response": recaptchaToken,
      }),
    })
      .then((res) => res.json())
      .then((res) => {
        if (res.code === 200) {
          setJustSubmitted(true)
          clearAllFormFields()
          if (widgetIdRef.current !== null) {
            window.grecaptcha.reset(widgetIdRef.current)
          }
          //setTimeout(() => setJustSubmitted(false), 2000)
        } else {
          setError(res.message)
        }
      })
      .catch((err) => setError(err.message || "Something went wrong"))
      .finally(() => setIsRequestPending(false))
  }

  return (
    <form className="form-container" onSubmit={submitFormData} method="POST">
      {justSubmitted ? (
        <div className="success-message">
          <h3>Sikeres üzenetküldés</h3>
          <p>
            Köszönöm, hogy írtál! Az üzeneted megérkezett, hamarosan felveszem
            veled a kapcsolatot.
          </p>
        </div>
      ) : (
        <>
          <div className="form-decor-container">
            <img
              src={connectFormDecor}
              alt="Connect Form Decor"
              className="form-decor"
            />
            <img
              src={connectFormDecor2}
              alt="Connect Form Decor"
              className="form-decor2"
            />

            <img
              src={connectFormDecor3}
              alt="connect-form-decor3"
              className="form-decor3"
            />
          </div>
          <InputField
            id="name"
            name="name"
            onChange={setName}
            value={name}
            labelText={ts.pages.home.connectSection.nameLabel}
            required
          />
          <InputField
            id="phoneNumber"
            name="phone"
            type="tel"
            onChange={setPhoneNumber}
            value={phoneNumber}
            labelText={ts.pages.home.connectSection.phoneNumberLabel}
            required
            pattern="^[+]?[0-9\s\-\(\)]{9,}$"
            title={ts.pages.home.connectSection.invalidPhoneNumberTitle}
          />
          <InputField
            id="email"
            name="email"
            type="email"
            onChange={setEmail}
            value={email}
            labelText={ts.pages.home.connectSection.emailLabel}
            required
          />
          <Dropdown
            id="service"
            name="service"
            onChange={setService}
            value={service}
            labelText={ts.pages.home.connectSection.serviceLabel}
            options={[...ts.pages.home.connectSection.serviceOptions]}
          />
          <TextArea
            id="message"
            name="message"
            onChange={setMessage}
            value={message}
            labelText="Üzenet"
          />
          <div className="bottom-section">
            <div
              ref={recaptchaRef}
              className={classNames("g-recaptcha", {
                invisible: !allRequiredFieldsFilled,
              })}
            ></div>

            <Button
              type="submit"
              text={
                justSubmitted
                  ? "Elküldve"
                  : isRequestPending
                  ? "Küldés folyamatban..."
                  : "Elküldöm"
              }
              size={ButtonSize.MD}
              style={{ width: "100%" }}
              className={classNames(
                justSubmitted
                  ? "button--success"
                  : isRequestPending
                  ? "button--pending"
                  : "",
              )}
              disabled={justSubmitted}
            />

            {error && (
              <div className="error-block">
                {ts.pages.home.connectSection.formSubmitError}
              </div>
            )}
          </div>{" "}
        </>
      )}
    </form>
  )
}
